import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import PaymentTable from "../../components/dashboard/PaymentTable";
import {
  getPayments,
  managePaymentQueryOptions,
  PaymentTransaction,
} from "../../queryOptions/ManagePaymentOptions";

const Payments = () => {
  const [payments, setPayments] = useState<PaymentTransaction[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const { data, refetch, isFetching } = useQuery(managePaymentQueryOptions());

  useEffect(() => {
    if (data) {
      setPayments(data.transactions || []);
      setHasMore(Boolean(data.hasMore));
      setNextCursor(data.nextCursor ?? null);
      setLoading(false);
    }
  }, [data]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setLoading(true);
    try {
      const result = await refetch();
      if (result.data) {
        setPayments(result.data.transactions || []);
        setHasMore(Boolean(result.data.hasMore));
        setNextCursor(result.data.nextCursor ?? null);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await getPayments(nextCursor);
      setPayments((prev) => {
        const existingIds = new Set(prev.map((p) => p.chargeId));
        const appended = (page.transactions || []).filter(
          (p) => !existingIds.has(p.chargeId)
        );
        return [...prev, ...appended];
      });
      setHasMore(Boolean(page.hasMore));
      setNextCursor(page.nextCursor ?? null);
    } catch (error) {
      console.error("Error loading more payments", error);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Paper
        elevation={4}
        sx={{
          p: 3,
          borderRadius: 3,
          backgroundColor: "background.paper",
          boxShadow: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            mb: 3,
            gap: 2,
          }}
        >
          <Typography variant="h5" component="h1" fontWeight="bold" color="primary">
            Manage Payments
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={
              refreshing || isFetching ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RefreshIcon />
              )
            }
            onClick={handleRefresh}
            disabled={refreshing || loadingMore}
            sx={{
              fontWeight: "bold",
              px: 3,
              borderRadius: 2,
            }}
          >
            Refresh
          </Button>
        </Box>

        <PaymentTable data={payments} loading={loading} />

        {hasMore && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={loadingMore}
              startIcon={
                loadingMore ? <CircularProgress size={18} color="inherit" /> : undefined
              }
            >
              {loadingMore ? "Loading..." : "Load more"}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Payments;
